import {Component, Inject, OnInit} from '@angular/core';
import {environment} from "../../../environments/environment";
import {UserService} from "../../services/user.service";
import {MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService} from "@azure/msal-angular";
import {NgxSpinnerService} from "ngx-spinner";
import {SweetAlertService} from "../../services/sweet-alert.service";
import {InteractionType} from "@azure/msal-browser";
import {HttpErrorResponse} from "@angular/common/http";
import {ActivatedRoute} from "@angular/router";

@Component({
  selector: 'app-anonymous-verify-passport',
  templateUrl: './anonymous-verify-passport.component.html',
  styleUrls: ['./anonymous-verify-passport.component.scss']
})
export class AnonymousVerifyPassportComponent implements OnInit {
  // Configuration from environment
  private readonly CLIENT_ID = environment.passportVerification.clientId;
  private readonly REDIRECT_URI = environment.passportVerification.anonymousRedirectUri;
  private readonly AUTH_URL = environment.passportVerification.authUrl;
  private readonly SCOPE = environment.passportVerification.scope;
  verificationFailed = false;
  verificationSuccess = false;


  // Section expansion states
  sections = {
    secure: true,
    required: false,
    privacy: false,
    storage: false,
    security: false
  };

  guid: string | null = null;

  constructor(
    private userService: UserService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private spinner: NgxSpinnerService,
    private sweetAlertService: SweetAlertService,
    private route: ActivatedRoute
  ) {}

  consentChecked = false;


  toggleSection(sectionName: string): void {
    if (this.sections.hasOwnProperty(sectionName)) {
      this.sections[sectionName as keyof typeof this.sections] = !this.sections[sectionName as keyof typeof this.sections];
    }
  }

  ngOnInit() {
    // Get the guid parameter from the route
    this.route.paramMap.subscribe(params => {
      this.guid = params.get('guid');
    });

    // Check if we're returning from OAuth callback
    this.handleCallback();
  }

  base64UrlEncode(input: any) {
        const bytes = new Uint8Array(input);
        let str = '';
        bytes.forEach((byte) => {
            str += String.fromCharCode(byte);
        });
        // Convert to Base64
        const base64 = btoa(str);
        // Convert Base64 to Base64Url by replacing characters
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }

  startAuthFlow() {
    this.spinner.show();
    this.verificationFailed = false;

    const csrfToken = this.generateRandomString(32);
    const stateData = {
        cid: this.guid,
        csrfToken: csrfToken
    };

    const encodedState = this.base64UrlEncode(new TextEncoder().encode(JSON.stringify(stateData)));

    localStorage.setItem('oauth_state', encodedState);

    // Generate code verifier and challenge for PKCE (if supported)
    const codeVerifier = this.generateRandomString(64);
    localStorage.setItem('code_verifier', codeVerifier);

    // Build authorization URL
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.CLIENT_ID,
      redirect_uri: this.REDIRECT_URI,
      scope: this.SCOPE,
      state: encodedState,
      provider: 'passport-reader',
      // Add code_challenge if PKCE is required
      // code_challenge: this.generateCodeChallenge(codeVerifier),
      // code_challenge_method: 'S256'
    });

    const authUrl = `${this.AUTH_URL}?${params.toString()}`;

    // Redirect to authorization server
    window.location.href = authUrl;
  }

  private handleCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      this.showErrorModal('Authentication Error', `Authentication error: ${error}`);
      return;
    }


    if (code && state) {
      // Verify state parameter


      let contactId = null;
      try {
          const decodedStateJson = new TextDecoder().decode(Uint8Array.from(atob(state.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)));
          const stateObject = JSON.parse(decodedStateJson);
          // In a real app, you would also validate stateObject.csrfToken against a stored value.
          // For simplicity here, we rely on the full `receivedState === storedState` check for CSRF.
          contactId = stateObject.cid;

      } catch (e) {
          console.error("Error decoding state parameter:", e);
          this.showErrorModal('Security Error', "Error processing state parameter. Possible tampering or malformed state.");

          localStorage.removeItem('oauth_state');
          localStorage.removeItem('code_verifier');
          return;
      }

      this.sendCodeToBackend(code, contactId);
    }
  }

  signOutAzureB2C() {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.msalService.logoutPopup({
        mainWindowRedirectUri: "/login"
      });
    } else {
      this.msalService.logoutRedirect();
    }
  }

  clearError() {
    this.verificationFailed = false;
    this.verificationSuccess = false;
  }

  private showErrorModal(title: string, message: string, icon: 'error' | 'warning' = 'error') {
    return icon === 'error'
      ? this.sweetAlertService.error(title, message)
      : this.sweetAlertService.warning(title, message);
  }


  private generateRandomString(length: number): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  private sendCodeToBackend(code: string, contactId : string) {
    this.spinner.show();

    this.userService.verifyWithContactId(code, contactId).subscribe({
      next: response => {
        if(response.result && response.item){
          this.verificationSuccess = true;
        }else{
          this.verificationFailed = true;
        }

        this.spinner.hide();
      },
      error: (error: HttpErrorResponse) => {
        console.error('Verification error:', error);


        this.spinner.hide();


        if (error.status === 400) {

          this.showErrorModal(
            'Bad Request',
            'The verification request was invalid. Please try again with a valid code.'
          );
        } else {

          this.showErrorModal(
            'Error',
            'Failed to verify passport. Please contact the administrator for assistance.'
          );
        }
        this.verificationFailed = true;
      }
    });


  }
}
